// ============================================
// FILE: src/features/lockers/components/LockerList.tsx
// ============================================

import React, { useMemo, useState, useEffect } from 'react';
import {
  List,
  TextField,
  FunctionField,
  useTranslate,
  ReferenceInput,
  SelectInput,
  ListProps,
  usePermissions,
  useGetIdentity,
  useGetList,
} from 'react-admin';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { Hotel as HotelIcon, Lock as LockIcon, Router as RouterIcon } from '@mui/icons-material';
import CustomDataGrid from '../../../components/common/CustomDatagrid';
import LockerActions from './LockerActions';
import { LockerStatusChip } from './ui/LockerStatusChip';
import { LockerAssignmentInfo } from './ui/LockerAssignmentInfo';
import { 
  Locker, 
  LockerStatus, 
} from '../types';
import { CustomSelectInputFilter, CustomTextInputFilter } from '@/components/common/CustomFilters';
import CustomListToolbar from '@/components/common/CustomListToolbar';
import { canEdit } from '@/utils/permissions';

// ============================================
// COMPONENTS
// ============================================

/**
 * Locker number field with icon
 */
const LockerNumberField: React.FC<{ record?: Locker }> = ({ record }) => {
  if (!record) return null;
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {record.displayNumber}
      </Typography>
    </Box>
  );
};

/**
 * Controller info field
 */
const ControllerField: React.FC<{ record?: Locker }> = ({ record }) => {
  if (!record?.controller) return <Typography variant="caption" color="text.secondary">-</Typography>;
  
  return (
    <Tooltip title={`${record.controller.ipAddress}:${record.controller.tcpPort}`}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption">
          {record.controller.name}
        </Typography>
      </Box>
    </Tooltip>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

const LockerList: React.FC<ListProps> = (props) => {
  const translate = useTranslate();
  const { permissions } = usePermissions();
  const { data: identity, isLoading: identityLoading } = useGetIdentity();
  
  // Estado local para el hotel seleccionado
  const [selectedHotelId, setSelectedHotelId] = useState<string | null>(null);
  
  // Determinar permisos y filtros según rol
  const userRole = permissions || '';
  const isSuperAdmin = userRole === 'SuperAdmin';
  const isGroupAdmin = userRole === 'GroupAdmin';
  const isHotelAdmin = userRole === 'HotelAdmin';
  const isReceptionist = userRole === 'Receptionist';
  
  // Check permissions
  const canCreate = useMemo(() => {
    return ['SuperAdmin'].includes(userRole);
  }, [userRole]);
  const baseCanEdit = canEdit(userRole, 'lockers');
  const canExport = true;
  
  // Determinar el filtro de hoteles según el rol
  const hotelFilter = useMemo(() => {
    if (isHotelAdmin || isReceptionist) {
      // Hotel fijo del usuario
      return identity?.hotelId ? { id: identity.hotelId } : {};
    } else if (isGroupAdmin) {
      // Solo hoteles del grupo
      return identity?.groupId ? { groupId: identity.groupId } : {};
    }
    // SuperAdmin ve todos
    return {};
  }, [isHotelAdmin, isReceptionist, isGroupAdmin, identity?.hotelId, identity?.groupId]);
  
  // Cargar hoteles disponibles según el rol
  const { data: hotels } = useGetList('hotels', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
    filter: hotelFilter,
  }, {
    enabled: !identityLoading,
  });
  
  // Establecer hotel inicial cuando se cargan los datos
  useEffect(() => {
    if (!identityLoading && hotels && hotels.length > 0 && !selectedHotelId) {
      // Para HotelAdmin/Receptionist, su hotel está fijo
      if ((isHotelAdmin || isReceptionist) && identity?.hotelId) {
        setSelectedHotelId(identity.hotelId);
      } else {
        // Para SuperAdmin/GroupAdmin, seleccionar el primero
        setSelectedHotelId(hotels[0].id);
      }
    }
  }, [identityLoading, identity?.hotelId, hotels, selectedHotelId, isHotelAdmin, isReceptionist]);
  
  // Define filters con hotel siempre visible
  const filters = useMemo(() => [
   
    
    // Búsqueda
    <CustomTextInputFilter
      key="search"
      label={translate('resources.lockers.filters.search')}
      source="q"
      alwaysOn
      sx={{ minWidth: 300 }}
    />,
    
     // SELECTOR DE HOTEL - Siempre visible pero con opciones según rol
    <ReferenceInput
      key="hotelId"
      source="hotelId"
      reference="hotels"
      label={translate('common.hotel')}
      alwaysOn
      sort={{ field: 'name', order: 'ASC' }}
      filter={hotelFilter}
      perPage={100}
    >
      <CustomSelectInputFilter 
        optionText="name"
        optionValue="id"
        disabled={isHotelAdmin || isReceptionist} // Deshabilitado para roles de hotel único
      />
    </ReferenceInput>,

    // Zona - filtrada por hotel seleccionado
    <ReferenceInput
      key="zoneId"
      source="zoneId"
      reference="zones"
      label={translate('resources.lockers.fields.zone')}
      filter={selectedHotelId ? { hotelId: selectedHotelId } : {}}
      alwaysOn
    >
      <CustomSelectInputFilter optionText="name" />
    </ReferenceInput>,
    
    // Tipo de taquilla - filtrado por hotel seleccionado
    <ReferenceInput
      key="lockerTypeId"
      source="lockerTypeId"
      reference="locker-types"
      label={translate('resources.lockers.fields.lockerType')}
      filter={selectedHotelId ? { hotelId: selectedHotelId } : {}}
      alwaysOn
    >
      <CustomSelectInputFilter optionText="name" />
    </ReferenceInput>,
    
    // Estado
    <CustomSelectInputFilter
      key="status"
      source="status"
      label={translate('resources.lockers.fields.status')}
      choices={[
        { id: LockerStatus.Available, name: translate('resources.lockers.status.available') },
        { id: LockerStatus.Occupied, name: translate('resources.lockers.status.occupied') },
        { id: LockerStatus.Reserved, name: translate('resources.lockers.status.reserved') },
        { id: LockerStatus.Blocked, name: translate('resources.lockers.status.blocked') },
      ]}
      alwaysOn
    />,
  ], [translate, selectedHotelId, hotelFilter, isHotelAdmin, isReceptionist]);
  
  // Valores por defecto del filtro - siempre incluir hotelId
  const filterDefaultValues = useMemo(() => {
    if (selectedHotelId) {
      return { hotelId: selectedHotelId };
    }
    return {};
  }, [selectedHotelId]);
  
  // Si no hay hotel seleccionado todavía, mostrar loading
  if (identityLoading || !selectedHotelId) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
 
  // Main list render
  return (
    <List
      {...props}
      title={translate('resources.lockers.titles.list')}
      filters={filters}
      filterDefaultValues={filterDefaultValues}
      sort={{ field: 'displayNumber', order: 'ASC' }}
      perPage={25}
      // Key para forzar re-render cuando cambia el hotel
      key={`lockers-${selectedHotelId}`}
      actions={
        <CustomListToolbar 
          showFilterButton={false}
          showCreate={canCreate}
          createLabel={translate('resources.lockers.actions.create', 'Nueva Taquilla')}
          showExport={canExport}
        />
      }
    >
      <CustomDataGrid
        rowClick="edit"
        bulkActionButtons={canCreate ? undefined : false}
      >
       
        {baseCanEdit && (
          
          <FunctionField
            label=""
            render={(record: Locker) => <LockerActions record={record} />}
            sortable={false}
          />
        )}

        {/* Locker number */}
        <FunctionField
          label={translate('resources.lockers.fields.displayNumber')}
          render={(record: Locker) => <LockerNumberField record={record} />}
          sortBy="displayNumber"
        />
        
         {/* Status */}
        <FunctionField
          label={translate('resources.lockers.fields.status')}
          render={(record: Locker) => (
            <LockerStatusChip status={record.status} />
          )}
          sortBy="status"
        />

        {/* Hardware ID */}
        <TextField 
          source="hardwareId" 
          label={translate('resources.lockers.fields.hardwareId')}
        />
        
        {/* Zone */}
        <TextField 
          source="zone.name" 
          label={translate('resources.lockers.fields.zone')}
          sortBy="zone.name"
        />
        
        {/* Locker Type */}
        <TextField 
          source="lockerType.name" 
          label={translate('resources.lockers.fields.lockerType')}
          sortBy="lockerType.name"
        />
        
        {/* Controller */}
        <FunctionField
          label={translate('resources.lockers.fields.controller')}
          render={(record: Locker) => <ControllerField record={record} />}
          sortable={false}
        />
        
       
        
        {/* Assignment info */}
        <FunctionField
          label={translate('resources.lockers.fields.assignment')}
          render={(record: Locker) => (
            <LockerAssignmentInfo record={record} />
          )}
          sortable={false}
        />
        
        {/* Reserved Until - Solo si está reservado */}
        <FunctionField
          label={translate('resources.lockers.fields.reservedUntil')}
          render={(record: Locker) => (
            record.reservedUntil ? (
              <Typography variant="caption" color="warning.main">
                {new Date(record.reservedUntil).toLocaleString()}
              </Typography>
            ) : (
              <Typography variant="caption" color="text.secondary">-</Typography>
            )
          )}
          sortable={false}
        />
        
        {/* Hotel - Solo mostrar si es SuperAdmin o GroupAdmin */}
        {(isSuperAdmin || isGroupAdmin) && (
          <TextField 
            source="hotel.name" 
            label={translate('resources.lockers.fields.hotel')}
            sortBy="hotel.name"
          />
        )}
        
      </CustomDataGrid>
    </List>
  );
};

export default LockerList;